import { useState } from 'react';
import { atualizarRubricaSchema, criarRubricaSchema, type ClasseRubrica, type RubricaResponse } from '@triade/contracts';
import { useAtualizarRubrica, useCriarRubrica, useRubricas } from '@/api/plano';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { CLASSES_ORDEM, ROTULO_CLASSE } from './custo-fabrica-mes';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';

const CAMPO = 'h-[34px] rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

/**
 * Gestao das rubricas do custo fabrica (criar, renomear, trocar classe, ativar/desativar).
 * Ver: plano.ver. Editar: plano.editar (sem ela, os campos ficam desabilitados).
 */
export function RubricasDialog({ podeEditar, onFechar }: { podeEditar: boolean; onFechar: () => void }) {
  const rubricas = useRubricas();
  const criar = useCriarRubrica();
  const [nome, setNome] = useState('');
  const [classe, setClasse] = useState<ClasseRubrica>('CUSTO_FIXO');
  const [erro, setErro] = useState<string | null>(null);

  async function criarRubrica() {
    setErro(null);
    const ordem = rubricas.data?.length ?? 0;
    const valido = criarRubricaSchema.safeParse({ nome, classe, ordem });
    if (!valido.success) {
      setErro(valido.error.issues[0]?.message ?? 'Dados da rubrica inválidos');
      return;
    }
    try {
      await criar.mutateAsync(valido.data);
      setNome('');
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onFechar()}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Rubricas do custo fábrica</DialogTitle>
          <DialogDescription>Nome e classe de cada rubrica. Rubrica inativa sai do previsto dos meses seguintes.</DialogDescription>
        </DialogHeader>

        {rubricas.isError && !rubricas.data ? (
          <ErroFormacao erro={rubricas.error} onTentar={() => void rubricas.refetch()} titulo="Não foi possível carregar as rubricas" />
        ) : !rubricas.data ? (
          <p role="status" className="text-small text-ink-muted">Carregando rubricas…</p>
        ) : (
          <ul className="flex flex-col gap-2" aria-label="Rubricas">
            {rubricas.data.map((r) => (
              <LinhaRubrica key={r.id} rubrica={r} podeEditar={podeEditar} />
            ))}
          </ul>
        )}

        {podeEditar && (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-border pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              void criarRubrica();
            }}
          >
            <div className="flex min-w-[200px] flex-1 flex-col gap-1">
              <label htmlFor="nova-rubrica-nome" className="text-caption font-medium text-ink-secondary">Nova rubrica</label>
              <input id="nova-rubrica-nome" className={CAMPO} value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80} autoComplete="off" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="nova-rubrica-classe" className="text-caption font-medium text-ink-secondary">Classe</label>
              <select id="nova-rubrica-classe" className={CAMPO} value={classe} onChange={(e) => setClasse(e.target.value as ClasseRubrica)}>
                {CLASSES_ORDEM.map((c) => (
                  <option key={c} value={c}>{ROTULO_CLASSE[c]}</option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={criar.isPending} className="h-[34px] rounded-[9px] px-3.5">
              {criar.isPending ? 'Criando…' : 'Criar rubrica'}
            </Button>
          </form>
        )}
        {erro && (
          <p role="alert" className="text-small text-danger-ink">{erro}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinhaRubrica({ rubrica, podeEditar }: { rubrica: RubricaResponse; podeEditar: boolean }) {
  const atualizar = useAtualizarRubrica();
  const [nome, setNome] = useState(rubrica.nome);
  const [classe, setClasse] = useState<ClasseRubrica>(rubrica.classe);
  const [erro, setErro] = useState<string | null>(null);
  const alterado = nome.trim() !== rubrica.nome || classe !== rubrica.classe;

  async function enviar(dto: { nome?: string; classe?: ClasseRubrica; ativa?: boolean }) {
    setErro(null);
    const valido = atualizarRubricaSchema.safeParse(dto);
    if (!valido.success) {
      setErro(valido.error.issues[0]?.message ?? 'Dados da rubrica inválidos');
      return;
    }
    try {
      await atualizar.mutateAsync({ id: rubrica.id, dto: valido.data });
    } catch (e) {
      setErro(mensagemApi(e));
    }
  }

  return (
    <li className="flex flex-wrap items-end gap-2 rounded-[10px] border border-border p-2.5" data-testid="linha-rubrica-gestao">
      <div className="flex min-w-[180px] flex-1 flex-col gap-1">
        <label htmlFor={`rubrica-nome-${rubrica.id}`} className="text-caption font-medium text-ink-secondary">Nome</label>
        <input id={`rubrica-nome-${rubrica.id}`} className={CAMPO} value={nome} disabled={!podeEditar} maxLength={80} onChange={(e) => setNome(e.target.value)} />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={`rubrica-classe-${rubrica.id}`} className="text-caption font-medium text-ink-secondary">Classe</label>
        <select id={`rubrica-classe-${rubrica.id}`} className={CAMPO} value={classe} disabled={!podeEditar} onChange={(e) => setClasse(e.target.value as ClasseRubrica)}>
          {CLASSES_ORDEM.map((c) => (
            <option key={c} value={c}>{ROTULO_CLASSE[c]}</option>
          ))}
        </select>
      </div>
      {podeEditar && (
        <div className="flex gap-1.5">
          <Button type="button" variant="outline" disabled={!alterado || atualizar.isPending} onClick={() => void enviar({ nome: nome.trim(), classe })} className="h-[34px] rounded-[9px] px-3">
            Salvar
          </Button>
          <Button type="button" variant="outline" disabled={atualizar.isPending} onClick={() => void enviar({ ativa: !rubrica.ativa })} className="h-[34px] rounded-[9px] px-3">
            {rubrica.ativa ? 'Desativar' : 'Ativar'}
          </Button>
        </div>
      )}
      {!rubrica.ativa && <span className="self-center text-caption text-ink-muted">Inativa</span>}
      {erro && <p role="alert" className="w-full text-small text-danger-ink">{erro}</p>}
    </li>
  );
}
